import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  useDisclosure,
} from "@chakra-ui/react";
import { MdOutlinePersonAddAlt1 } from "react-icons/md";
import Table from "../../../componant/Table/Table";
import { errorToast, successToast } from "../../../utils/toast";
import { superAdminApi } from "../../../services/superAdmin/api";
import AdminFormDrawer from "./components/AdminFormDrawer";

const SUMMARY_CARDS = [
  { key: "totalAdmins", label: "Total Admins" },
  { key: "activeAdmins", label: "Active Admins" },
  { key: "inactiveAdmins", label: "Inactive Admins" },
  { key: "superAdmins", label: "Super Admins" },
];

const initialFilters = {
  search: "",
  planStatus: "all",
  paymentStatus: "all",
  isSubscribed: "all",
};

const formatDateTime = (value) =>
  value
    ? new Intl.DateTimeFormat("en-IN", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "-";

const SummaryCard = ({ label, value }) => (
  <div className="rounded-3xl border border-orange-100 bg-white p-5 shadow-sm">
    <p className="text-sm font-medium text-gray-500">{label}</p>
    <h3 className="mt-2 text-3xl font-bold text-gray-900">{value}</h3>
  </div>
);

const PaymentStatusBadge = ({ status }) => (
  <span
    className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
      status === "PAID"
        ? "bg-green-50 text-green-700"
        : status === "FAILED"
          ? "bg-red-50 text-red-600"
          : "bg-yellow-50 text-yellow-700"
    }`}
  >
    {status || "-"}
  </span>
);

const SubscriptionBadge = ({ isSubscribed }) => (
  <span
    className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
      isSubscribed
        ? "bg-green-50 text-green-700"
        : "bg-gray-100 text-gray-600"
    }`}
  >
    {isSubscribed ? "Subscribed" : "Not Subscribed"}
  </span>
);

const getApiMessage = (response, fallbackMessage) =>
  response?.message || fallbackMessage;

const getApiErrorMessage = (error, fallbackMessage) =>
  error?.response?.data?.message || error?.message || fallbackMessage;

const getInstituteId = (admin = {}) =>
  admin?.institute?.id ||
  admin?.institute?._id ||
  admin?.institute?.instituteId ||
  admin?.instituteId ||
  admin?.id;

const AdminManagement = () => {
  const queryClient = useQueryClient();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [filters, setFilters] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [statusTarget, setStatusTarget] = useState(null);
  const [retryTarget, setRetryTarget] = useState(null);
  const [retryPassword, setRetryPassword] = useState("");
  const [retryPasswordError, setRetryPasswordError] = useState("");

  const adminsQuery = useQuery({
    queryKey: ["super-admin", "admins", filters, page],
    queryFn: async () => {
      const response = await superAdminApi.getAdmins({
        ...filters,
        page,
        limit: 10,
      });
      return response.result;
    },
    placeholderData: (previousData) => previousData,
  });

  const refreshAdmins = () =>
    queryClient.invalidateQueries({
      queryKey: ["super-admin", "admins"],
    });

  const createAdminMutation = useMutation({
    mutationFn: (payload) => superAdminApi.createAdmin(payload),
    onSuccess: (response) => {
      successToast(getApiMessage(response, "Admin created successfully"));
      refreshAdmins();
      handleCloseDrawer({ force: true });
    },
    onError: (error) => {
      errorToast(getApiErrorMessage(error, "Unable to create admin."));
    },
  });

  const updateAdminMutation = useMutation({
    mutationFn: ({ adminId, payload }) => superAdminApi.updateAdmin(adminId, payload),
    onSuccess: (response) => {
      successToast(getApiMessage(response, "Admin updated successfully"));
      refreshAdmins();
    },
    onError: (error) => {
      errorToast(getApiErrorMessage(error, "Unable to update admin."));
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ adminId, nextStatus }) =>
      superAdminApi.updateAdminStatus(adminId, nextStatus),
    onSuccess: (response) => {
      successToast(getApiMessage(response, "Admin status updated successfully"));
      refreshAdmins();
      setStatusTarget(null);
    },
    onError: (error) => {
      errorToast(getApiErrorMessage(error, "Unable to update admin status."));
    },
  });

  const retryAdhyayiProvisionMutation = useMutation({
    mutationFn: ({ adminId, password }) =>
      superAdminApi.retryAdhyayiProvision(adminId, password),
    onSuccess: (response) => {
      successToast(getApiMessage(response, "Adhyayi setup completed successfully."));
      refreshAdmins();
      setRetryTarget(null);
      setRetryPassword("");
      setRetryPasswordError("");
    },
    onError: (error) => {
      errorToast(getApiErrorMessage(error, "Unable to retry Adhyayi setup."));
    },
  });

  const handleFilterChange = (name, value) => {
    setPage(1);
    setFilters((currentFilters) => ({
      ...currentFilters,
      [name]: value,
    }));
  };

  const handleOpenCreate = () => {
    setSelectedAdmin(null);
    onOpen();
  };

  const handleOpenEdit = (admin) => {
    setSelectedAdmin(admin);
    onOpen();
  };

  const handleCloseDrawer = ({ force = false } = {}) => {
    if (!force && (createAdminMutation.isPending || updateAdminMutation.isPending)) {
      return;
    }

    setSelectedAdmin(null);
    onClose();
  };

  const handleSubmit = async (payload) => {
    if (selectedAdmin) {
      await updateAdminMutation.mutateAsync({
        adminId: getInstituteId(selectedAdmin),
        payload,
      });
      return;
    }

    await createAdminMutation.mutateAsync(payload);
  };

  const handleConfirmStatusChange = async () => {
    if (!statusTarget) {
      return;
    }

    await updateStatusMutation.mutateAsync({
      adminId: statusTarget.id,
      nextStatus: statusTarget.nextStatus,
    });
  };

  const handleOpenRetryAdhyayi = (admin) => {
    setRetryTarget(admin);
    setRetryPassword("");
    setRetryPasswordError("");
  };

  const handleCloseRetryAdhyayi = () => {
    if (retryAdhyayiProvisionMutation.isPending) {
      return;
    }

    setRetryTarget(null);
    setRetryPassword("");
    setRetryPasswordError("");
  };

  const handleRetryAdhyayiProvision = async () => {
    if (!retryTarget) {
      return;
    }

    if (!retryPassword.trim()) {
      setRetryPasswordError("Admin password is required.");
      return;
    }

    await retryAdhyayiProvisionMutation.mutateAsync({
      adminId: retryTarget.id,
      password: retryPassword,
    });
  };

  const activeFiltersCount = Object.values(filters).filter(
    (value) => value && value !== "all",
  ).length;

  const columns = useMemo(
    () => [
      {
        Header: "Admin",
        accessor: "fullName",
        Cell: ({ row }) => (
          <div className="text-left">
            <p className="font-semibold text-gray-900">{row.original.fullName}</p>
            <p className="text-xs text-gray-500">{row.original.email}</p>
          </div>
        ),
      },
      {
        Header: "Phone",
        accessor: "phoneNumber",
      },
      {
        Header: "Institute",
        accessor: "assignedTenantName",
        Cell: ({ row }) => (
          <div className="text-left">
            <p className="font-semibold text-gray-900">{row.original.assignedTenantName}</p>
            <p className="text-xs text-gray-500">{row.original.assignedAppName}</p>
            <p className="text-xs text-gray-500">{row.original.assignedInstituteName}</p>
          </div>
        ),
      },
      {
        Header: "User Count",
        accessor: "studentCount",
        Cell: ({ value }) => (
          <span className="font-semibold text-gray-900">{value ?? 0}</span>
        ),
      },
      {
        Header: "Subscription",
        accessor: "isSubscribed",
        Cell: ({ row }) => (
          <div className="text-left">
            <SubscriptionBadge isSubscribed={row.original.isSubscribed} />
            <p className="mt-1 text-xs text-gray-500">
              Plan: {row.original.planStatus || "-"}
            </p>
          </div>
        ),
      },
      {
        Header: "Payment",
        accessor: "paymentStatus",
        Cell: ({ row }) => (
          <div className="text-left">
            <PaymentStatusBadge status={row.original.paymentStatus} />
            <p className="mt-1 text-xs text-gray-500">
              Amount: {row.original.paymentAmount === "" ? "-" : `Rs. ${row.original.paymentAmount}`}
            </p>
          </div>
        ),
      },
      {
        Header: "Updated",
        accessor: "updatedAt",
        Cell: ({ value }) => (
          <span className="text-xs text-gray-600">{formatDateTime(value)}</span>
        ),
      },
      {
        Header: "Actions",
        accessor: "id",
        Cell: ({ row }) => {
          const nextStatus =
            row.original.status === "active" ? "inactive" : "active";

          return (
            <div className="flex flex-wrap justify-center gap-2">
              <button
                type="button"
                onClick={() => handleOpenEdit(row.original)}
                className="rounded-lg border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-700 transition hover:border-bgBlue hover:text-bgBlue"
              >
                Edit
              </button>
              {row.original.adhyayiProvisioning?.status === "provision_failed" && (
                <button
                  type="button"
                  onClick={() => handleOpenRetryAdhyayi(row.original)}
                  className="rounded-lg bg-yellow-500 px-3 py-2 text-xs font-semibold text-white transition hover:bg-yellow-600"
                >
                  Retry Adhyayi Setup
                </button>
              )}
              <button
                type="button"
                onClick={() =>
                  setStatusTarget({
                    ...row.original,
                    nextStatus,
                  })
                }
                className={`rounded-lg px-3 py-2 text-xs font-semibold text-white transition ${
                  nextStatus === "inactive"
                    ? "bg-red-500 hover:bg-red-600"
                    : "bg-green-600 hover:bg-green-700"
                }`}
              >
                {nextStatus === "inactive" ? "Deactivate" : "Activate"}
              </button>
            </div>
          );
        },
      },
    ],
    [],
  );

  const adminRows = adminsQuery.data?.items || [];
  const pagination = adminsQuery.data?.pagination;
  const summary = adminsQuery.data?.summary || {};

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col gap-4 rounded-[28px] border border-orange-100 bg-white p-6 shadow-sm lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-purple">
            Super Admin
          </p>
          <h1 className="mt-2 text-3xl font-bold text-gray-900">Admin Management</h1>
          <p className="mt-2 max-w-3xl text-sm text-gray-600">
            Manage institute admin users, subscription details, payments, and owner
            information through the live backend.
          </p>
        </div>

        <button
          type="button"
          onClick={handleOpenCreate}
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-bgBlue px-5 py-3 text-sm font-semibold text-white transition hover:opacity-90"
        >
          <MdOutlinePersonAddAlt1 size={20} />
          Create Admin
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {SUMMARY_CARDS.map((card) => (
          <SummaryCard
            key={card.key}
            label={card.label}
            value={summary[card.key] ?? 0}
          />
        ))}
      </div>

      <div className="rounded-[28px] border border-orange-100 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="grid flex-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div>
              <label className="mb-2 block text-sm font-semibold text-gray-700">
                Search
              </label>
              <input
                type="text"
                placeholder="Search by owner, email, or institute"
                value={filters.search}
                onChange={(event) => handleFilterChange("search", event.target.value)}
                className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none transition focus:border-bgBlue"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-semibold text-gray-700">
                Plan Status
              </label>
              <select
                value={filters.planStatus}
                onChange={(event) => handleFilterChange("planStatus", event.target.value)}
                className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none transition focus:border-bgBlue"
              >
                <option value="all">All plan statuses</option>
                <option value="ACTIVE">ACTIVE</option>
                <option value="INACTIVE">INACTIVE</option>
                <option value="EXPIRED">EXPIRED</option>
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-semibold text-gray-700">
                Payment Status
              </label>
              <select
                value={filters.paymentStatus}
                onChange={(event) => handleFilterChange("paymentStatus", event.target.value)}
                className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none transition focus:border-bgBlue"
              >
                <option value="all">All payment statuses</option>
                <option value="PAID">PAID</option>
                <option value="PENDING">PENDING</option>
                <option value="FAILED">FAILED</option>
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-semibold text-gray-700">
                Subscribed
              </label>
              <select
                value={filters.isSubscribed}
                onChange={(event) => handleFilterChange("isSubscribed", event.target.value)}
                className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none transition focus:border-bgBlue"
              >
                <option value="all">All subscriptions</option>
                <option value="true">Subscribed</option>
                <option value="false">Not subscribed</option>
              </select>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-xs font-medium uppercase tracking-[0.14em] text-gray-500">
              Live API
            </span>
            {activeFiltersCount > 0 && (
              <button
                type="button"
                onClick={() => {
                  setFilters(initialFilters);
                  setPage(1);
                }}
                className="rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold text-gray-700 transition hover:border-bgBlue hover:text-bgBlue"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>

        <div className="mt-6">
          {adminsQuery.isLoading ? (
            <div className="flex min-h-[280px] items-center justify-center rounded-3xl border border-dashed border-orange-200 bg-orange-50/40">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-purple border-t-transparent" />
            </div>
          ) : adminsQuery.isError ? (
            <div className="rounded-3xl border border-red-100 bg-red-50 p-8 text-center">
              <h2 className="text-xl font-semibold text-red-700">
                Unable to load admin data
              </h2>
              <p className="mt-2 text-sm text-red-600">
                The backend returned an error while fetching institute admins.
              </p>
              <button
                type="button"
                onClick={() => {
                  adminsQuery.refetch();
                }}
                className="mt-5 rounded-xl bg-red-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-red-700"
              >
                Retry
              </button>
            </div>
          ) : adminRows.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-orange-200 bg-orange-50/40 p-10 text-center">
              <h2 className="text-xl font-semibold text-gray-900">No admins found</h2>
              <p className="mt-2 text-sm text-gray-600">
                No admin records matched the current filters. Adjust the search or
                create a new admin assignment.
              </p>
              <div className="mt-6 flex justify-center gap-3">
                {activeFiltersCount > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setFilters(initialFilters);
                      setPage(1);
                    }}
                    className="rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold text-gray-700 transition hover:border-bgBlue hover:text-bgBlue"
                  >
                    Reset Filters
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleOpenCreate}
                  className="rounded-xl bg-bgBlue px-4 py-3 text-sm font-semibold text-white transition hover:opacity-90"
                >
                  Create Admin
                </button>
              </div>
            </div>
          ) : (
            <Table
              columns={columns}
              data={adminRows}
              pagination
              total={pagination?.totalItems}
              pageSize={pagination?.limit}
              currentPage={pagination?.page}
              onPageChange={setPage}
            />
          )}
        </div>
      </div>

      <AdminFormDrawer
        admin={selectedAdmin}
        isLoading={createAdminMutation.isPending || updateAdminMutation.isPending}
        isOpen={isOpen}
        onClose={handleCloseDrawer}
        onSubmit={handleSubmit}
      />

      <Modal
        isOpen={Boolean(statusTarget)}
        onClose={() => {
          if (!updateStatusMutation.isPending) {
            setStatusTarget(null);
          }
        }}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {statusTarget?.nextStatus === "inactive"
              ? "Deactivate Admin"
              : "Activate Admin"}
          </ModalHeader>
          <ModalCloseButton disabled={updateStatusMutation.isPending} />
          <ModalBody>
            <p className="text-sm text-gray-600">
              {statusTarget?.nextStatus === "inactive"
                ? `This will revoke active admin access for ${statusTarget?.fullName}.`
                : `This will restore admin access for ${statusTarget?.fullName}.`}
            </p>
            <div className="mt-4 rounded-2xl border border-gray-100 bg-gray-50 p-4">
              <p className="font-semibold text-gray-900">{statusTarget?.fullName}</p>
              <p className="mt-1 text-sm text-gray-500">{statusTarget?.email}</p>
              <p className="mt-1 text-sm text-gray-500">
                {statusTarget?.assignedTenantName} | {statusTarget?.assignedAppName}
              </p>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              mr={3}
              onClick={() => setStatusTarget(null)}
              isDisabled={updateStatusMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              colorScheme={statusTarget?.nextStatus === "inactive" ? "red" : "green"}
              onClick={handleConfirmStatusChange}
              isLoading={updateStatusMutation.isPending}
            >
              {statusTarget?.nextStatus === "inactive"
                ? "Deactivate"
                : "Activate"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={Boolean(retryTarget)}
        onClose={handleCloseRetryAdhyayi}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Retry Adhyayi Setup</ModalHeader>
          <ModalCloseButton disabled={retryAdhyayiProvisionMutation.isPending} />
          <ModalBody>
            <p className="text-sm text-gray-600">
              Admin create ho gaya hai, lekin Adhyayi setup fail ho gaya. Setup retry
              karne ke liye admin password enter karein.
            </p>
            <div className="mt-4 rounded-2xl border border-gray-100 bg-gray-50 p-4">
              <p className="font-semibold text-gray-900">{retryTarget?.fullName}</p>
              <p className="mt-1 text-sm text-gray-500">{retryTarget?.email}</p>
            </div>
            <FormControl className="mt-4" isInvalid={Boolean(retryPasswordError)}>
              <FormLabel>Admin Password</FormLabel>
              <Input
                type="password"
                value={retryPassword}
                onChange={(event) => {
                  setRetryPassword(event.target.value);
                  setRetryPasswordError("");
                }}
                placeholder="Enter admin password"
              />
              <FormErrorMessage>{retryPasswordError}</FormErrorMessage>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button
              mr={3}
              onClick={handleCloseRetryAdhyayi}
              isDisabled={retryAdhyayiProvisionMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              colorScheme="purple"
              onClick={handleRetryAdhyayiProvision}
              isLoading={retryAdhyayiProvisionMutation.isPending}
            >
              Retry
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default AdminManagement;
